/**
 * Vue 3 migration-build noise control.
 *
 * Import this ONCE, as the FIRST import touching Vue, in every entry point.
 *
 * Two rules govern this file:
 *  1. Only suppress warnings generated by vendor libraries we cannot fix
 *     (bootstrap-vue, vuelidate 0.7, vuejs-dialog, vue2-datepicker, ...).
 *     Warnings from OUR components are the migration worklist — leave them
 *     loud, fix them, then they disappear on their own.
 *  2. Always use 'suppress-warning', NEVER `false`. 'suppress-warning'
 *     keeps the Vue 2 behavior enabled and just silences the console;
 *     `false` disables the compat behavior itself and will break the
 *     vendor libraries that rely on it.
 *
 * All of this is dev-only; production builds strip compat warnings anyway.
 */
import Vue, { configureCompat } from 'vue';

configureCompat({
    // Vue 2 global API used by bootstrap-vue / vuelidate / vuejs-dialog
    GLOBAL_EXTEND: 'suppress-warning',
    GLOBAL_MOUNT: 'suppress-warning',
    GLOBAL_PROTOTYPE: 'suppress-warning',
    GLOBAL_SET: 'suppress-warning',
    GLOBAL_DELETE: 'suppress-warning',
    // Vue 2 component/option patterns used throughout the vendor libs
    INSTANCE_EVENT_EMITTER: 'suppress-warning',
    OPTIONS_BEFORE_DESTROY: 'suppress-warning',
    OPTIONS_DESTROYED: 'suppress-warning',
    COMPONENT_ASYNC: 'suppress-warning',
    COMPONENT_FUNCTIONAL: 'suppress-warning',
    CUSTOM_DIR: 'suppress-warning',
    RENDER_FUNCTION: 'suppress-warning',
    WATCH_ARRAY: 'suppress-warning',
    INSTANCE_SCOPED_SLOTS: 'suppress-warning',
    INSTANCE_LISTENERS: 'suppress-warning',
    INSTANCE_ATTRS_CLASS_STYLE: 'suppress-warning',
    OPTIONS_DATA_MERGE: 'suppress-warning',
    CONFIG_WHITESPACE: 'suppress-warning',
    COMPONENT_V_MODEL: 'suppress-warning',
    ATTR_FALSE_VALUE: 'suppress-warning',
    PRIVATE_APIS: 'suppress-warning',
    INSTANCE_DESTROY: 'suppress-warning',
});

// Non-compat warnings that fire constantly from the vendor stack in dev.
// Anything not matching these exact patterns still prints normally.
const IGNORED_WARN_PATTERNS = [
    /has already been registered in target app/,
    /provide\(\) can only be used inside setup\(\)/,
];

if (process.env.NODE_ENV !== 'production') {
    const originalWarnHandler = Vue.config.warnHandler;
    Vue.config.warnHandler = function (msg, vm, trace) {
        if (IGNORED_WARN_PATTERNS.some((re) => re.test(msg))) return;
        if (originalWarnHandler) {
            originalWarnHandler.call(this, msg, vm, trace);
        } else {
            // eslint-disable-next-line no-console
            console.warn(`[Vue warn]: ${msg}${trace || ''}`);
        }
    };
}
